import { useState } from 'react';
import { shiftAPI, getApiErrorMessage } from '@matix/api-client';
import type {
  ShiftReceiptGroupDto,
  ShiftReceiptRowDto,
  ShiftSummaryDto,
} from '@matix/api-client';
import { POS_PERMISSIONS } from '@matix/types';
import { fmt } from '../../lib/mock-data';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { useCanI } from '../../lib/permissions';
import { PaymentMethodEditDialog } from './PaymentMethodEditDialog';

/**
 * Receipts drill-down inside the close-shift screen — Phase 10.9.
 *
 * Two tabs: "By type" and "By payment". Each tab lists the groups and
 * expands the rows on click. Clicking a row opens the payment-method
 * edit dialog. After a successful edit the parent's summary is refreshed
 * via re-render — we re-fetch and lift state up by reloading via prop.
 */

interface Props {
  summary: ShiftSummaryDto;
}

type Tab = 'by_type' | 'by_payment';

export function ShiftReceiptDrillDown({ summary: initial }: Props) {
  const { t } = useLang();
  const [summary, setSummary] = useState<ShiftSummaryDto>(initial);
  const [tab, setTab] = useState<Tab>('by_type');
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [editing, setEditing] = useState<ShiftReceiptRowDto | null>(null);

  // Legacy parity: "Change payment method" gates the per-receipt edit action.
  const canChangePayment = useCanI(POS_PERMISSIONS.ChangePaymentMethod);

  const groups = tab === 'by_type'
    ? summary.receipts_by_type
    : summary.receipts_by_payment;

  async function refreshSummary() {
    try {
      const next = await shiftAPI.summary();
      if (next) setSummary(next);
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
    }
  }

  return (
    <div>
      <div
        style={{
          display: 'inline-flex',
          gap: 4,
          marginBottom: 12,
          background: 'var(--surface-3)',
          padding: 4,
          borderRadius: 8,
        }}
      >
        <button
          type="button"
          className={`tab ${tab === 'by_type' ? 'active' : ''}`}
          onClick={() => { setTab('by_type'); setOpenGroup(null); }}
        >
          {t({ en: 'By order type', ar: 'حسب نوع الطلب' })}
        </button>
        <button
          type="button"
          className={`tab ${tab === 'by_payment' ? 'active' : ''}`}
          onClick={() => { setTab('by_payment'); setOpenGroup(null); }}
        >
          {t({ en: 'By payment method', ar: 'حسب طريقة الدفع' })}
        </button>
      </div>

      {groups.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          {t({ en: 'No receipts in this shift yet.', ar: 'لا توجد فواتير في هذه الوردية بعد.' })}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 4 }}>
          {groups.map((g) => (
            <DrillGroup
              key={String(g.key)}
              group={g}
              expanded={openGroup === String(g.key)}
              onToggle={() => setOpenGroup(openGroup === String(g.key) ? null : String(g.key))}
              onEditRow={(r) => setEditing(r)}
              canEditPayment={canChangePayment}
            />
          ))}
        </div>
      )}

      {editing && (
        <PaymentMethodEditDialog
          receiptId={editing.id}
          receiptCode={editing.code}
          currentPaymentTypeId={editing.payment_type}
          currentPaymentLabel={editing.payment_label}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refreshSummary();
          }}
        />
      )}
    </div>
  );
}

function DrillGroup({
  group, expanded, onToggle, onEditRow, canEditPayment,
}: {
  group: ShiftReceiptGroupDto;
  expanded: boolean;
  onToggle: () => void;
  onEditRow: (r: ShiftReceiptRowDto) => void;
  canEditPayment: boolean;
}) {
  const { t } = useLang();
  return (
    <div
      className="drill-group"
      style={{
        border: '1px solid var(--border)',
        borderRadius: 6,
        background: 'var(--surface-2)',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          color: 'var(--text)',
          font: 'inherit',
        }}
      >
        <span style={{ fontWeight: 600, color: 'var(--text)' }}>
          {expanded ? '▾' : '▸'} {group.label}
        </span>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {t({ en: `${group.count} receipts`, ar: `${group.count} فاتورة` })} · {fmt(group.total)}
        </span>
      </button>
      {expanded && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          {group.receipts.length === 0 ? (
            <div style={{ padding: 8, fontSize: 12, color: 'var(--text-muted)' }}>
              {t({ en: 'No receipts in this group.', ar: 'لا توجد فواتير في هذه المجموعة.' })}
            </div>
          ) : (
            <table
              style={{
                width: '100%',
                fontSize: 13,
                borderCollapse: 'collapse',
                color: 'var(--text)',
              }}
            >
              <thead>
                <tr style={{ color: 'var(--text-muted)', background: 'var(--surface-3)' }}>
                  <th style={TH}>{t({ en: 'Invoice No.', ar: 'رقم الفاتورة' })}</th>
                  <th style={TH}>{t({ en: 'Date & time', ar: 'التاريخ والوقت' })}</th>
                  <th style={TH}>{t({ en: 'Payment', ar: 'الدفع' })}</th>
                  <th style={TH}>{t({ en: 'Total', ar: 'الإجمالي' })}</th>
                  <th style={{ ...TH, width: 50 }} />
                </tr>
              </thead>
              <tbody>
                {group.receipts.map((r) => (
                  <tr
                    key={r.id}
                    style={
                      r.is_canceled
                        ? { color: 'var(--text-muted)', textDecoration: 'line-through' }
                        : { color: 'var(--text)' }
                    }
                  >
                    <td style={TD}>
                      {r.code}
                      {r.is_return && (
                        <span className="return-chip">{t({ en: 'RETURN', ar: 'مرتجع' })}</span>
                      )}
                    </td>
                    <td style={{ ...TD, direction: 'ltr', unicodeBidi: 'isolate' }}>{formatDateTime(r.time)}</td>
                    <td style={TD}>{r.payment_label ?? '—'}</td>
                    <td
                      style={{
                        ...TD,
                        color: r.total < 0 ? 'var(--danger)' : undefined,
                      }}
                    >
                      {fmt(r.total)}
                    </td>
                    <td style={TD}>
                      {!r.is_canceled && canEditPayment && (
                        <button
                          type="button"
                          onClick={() => onEditRow(r)}
                          title={t({ en: 'Change payment method', ar: 'تغيير طريقة الدفع' })}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--primary)',
                            cursor: 'pointer',
                            font: 'inherit',
                            padding: 0,
                          }}
                        >
                          {t({ en: 'edit', ar: 'تعديل' })}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const TH = { textAlign: 'center', padding: '6px 10px', fontWeight: 500 } as const;
const TD = { textAlign: 'center', padding: '6px 10px' } as const;

function formatDateTime(iso: string): string {
  // Backend ships Entry_Date as a naive tenant-TZ string; parse the wall-clock
  // components from the string so the displayed date and time match the
  // cashier's and the receipt's, even when host and tenant timezones differ.
  const naive = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})/.exec(iso.trim());
  let date: string, hour24: number, minute: number;
  if (naive) {
    date = naive[1]!; hour24 = Number(naive[2]); minute = Number(naive[3]);
  } else {
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) return iso;
    const d = new Date(ms);
    date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    hour24 = d.getHours(); minute = d.getMinutes();
  }
  const suffix = hour24 >= 12 ? 'PM' : 'AM';
  const h12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${date} ${String(h12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${suffix}`;
}
